// Ispis stanja skladista u pdf.
//
// The pdf prints exactly the rows the list is showing - same scope, same group,
// same search, same column groups - so what is on paper matches the screen. It is
// kept out of the component because the column widths need real arithmetic: with
// both column groups on, sixteen columns have to fit an A4 landscape page.

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'

const LINE = '#9e9e9e'
const HEAD_BG = '#eeeeee'
const TOTAL_BG = '#e8e8e8'

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

const novac = (v) => num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')

// Quantities are usually whole, so 12 stays 12 and 12,5 keeps its decimals.
const kol = (v) => {
  const n = num(v)
  const dec = Math.abs(n - Math.round(n)) < 0.0001 ? 0 : 2
  return n.toFixed(dec).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

// null means there was no nabava to divide by, so a dash rather than 0 %
const posto = (v) => (v === null || v === undefined
  ? '–'
  : num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '%')

// counts print as plain integers, no thousands separator games
const cijeli = (v) => String(v === null || v === undefined ? '' : v)

const datum = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '')

// A4 landscape minus the page margins.
const PAGE_WIDTH = 841.89 - 2 * 18
const V_LINE = 0.4

// pdfmake adds the cell padding and the vertical lines on top of the widths given
// in `widths`. The layout below sets the padding to zero and pads through the text
// margins instead, so only the lines have to be taken off the budget - without this
// sixteen columns run off the right edge of the page.
const usableWidth = (columnCount) => PAGE_WIDTH - (columnCount + 1) * V_LINE

/**
 * Column set mirrors the two toggles on screen.
 * `w` is the printed width, `min` is what the flexible Naziv column may shrink to.
 */
const buildColumns = (promet, zarada, grupe, naziv, samoVrijednost, poProdajnoj) => {
  // Nazivi kolona koje ovise o osnovici (dobavne / bez PDV-a) stizu s ekrana,
  // da se ispis i ekran ne mogu razici. Bez njih ostaju dosadasnji nazivi.
  const n = Object.assign({
    ulazVr: 'Ulazna dobavna vrijednost',
    stanjeVr: 'Stanje po dobavnoj cijeni',
    prosjCijena: 'Prosj. NC',
    izlazVr: 'Izlaz vr.'
  }, naziv || {})
  // The group sheet carries no sifra, naziv, JM or quantities: quantities of
  // articles measured in kom, m and kg cannot be added together, so the article
  // count takes their place.
  // Isti redoslijed i isti nazivi kao na ekranu: prvo sve KOLICINE
  // (poc. stanje -> ulaz -> izlaz -> stanje), pa VRIJEDNOSTI, pa RUC.
  if (grupe) {
    const g = [
      {key: 'grupa', head: 'Grupa', w: 0, align: 'left', flex: true},
      {key: 'artikala', head: 'Artikala', w: 44, align: 'right', fmt: cijeli},
      {key: 'saStanjem', head: 'Sa stanjem', w: 50, align: 'right', fmt: cijeli}
    ]
    if (samoVrijednost) {
      g.push(
        {key: 'zalihaBezPdv', head: 'Vrijednost bez PDV-a', w: 70, align: 'right', fmt: novac},
        {key: 'zalihaSPdv', head: 'Vrijednost s PDV-om', w: 70, align: 'right', fmt: novac}
      )
      return rasporedi(g)
    }
    if (promet) {
      g.push({key: 'ulazVr', head: n.ulazVr, w: 62, align: 'right', fmt: novac})
      if (poProdajnoj) {
        g.push({key: 'izlazVr', head: n.izlazVr, w: 68, align: 'right', fmt: novac})
      }
    }
    g.push({key: 'stanjeVr', head: n.stanjeVr, w: 62, align: 'right', fmt: novac})
    if (promet && !poProdajnoj) {
      g.push({key: 'izlazVr', head: n.izlazVr, w: 68, align: 'right', fmt: novac})
    }
    return rasporedi(g)
  }

  const cols = [
    {key: 'itemID', head: 'Šifra', w: 54, align: 'left'},
    {key: 'itemName', head: 'Naziv', w: 0, align: 'left', flex: true},
    {key: 'itemUnit', head: 'JM', w: 26, align: 'left'},
    // narrowed to match the list; the width freed up flows into Naziv below
    {key: 'grupa', head: 'Grupa', w: 48, align: 'left'}
  ]

  // --- kolicine ---
  cols.push({key: 'pocKol', head: 'Poč. st.', w: 40, align: 'right', fmt: kol})
  if (promet) {
    cols.push(
      {key: 'ulazKol', head: 'Ulaz kol.', w: 42, align: 'right', fmt: kol},
      {key: 'izlazKol', head: 'Izlaz kol.', w: 42, align: 'right', fmt: kol}
    )
  }
  cols.push({key: 'stanjeKol', head: 'Stanje', w: 40, align: 'right', fmt: kol})

  // Trenutna vrijednost zalihe po cijenama iz sifrarnika. Uz svaku vrijednost
  // ide i cijena, inace se na papiru ne vidi odakle iznos.
  if (samoVrijednost) {
    cols.push(
      {key: 'katPC', head: 'Cijena bez PDV-a', w: 60, align: 'right', fmt: novac},
      {key: 'zalihaBezPdv', head: 'Vrijednost bez PDV-a', w: 70, align: 'right', fmt: novac},
      {key: 'katPCsPdv', head: 'Cijena s PDV-om', w: 60, align: 'right', fmt: novac},
      {key: 'zalihaSPdv', head: 'Vrijednost s PDV-om', w: 70, align: 'right', fmt: novac}
    )
    return rasporedi(cols)
  }

  // --- vrijednosti ---
  // Vrijednosti idu istim redom kao kolicine iznad njih: ulaz, izlaz, stanje.
  // U dobavnoj osnovici "izlaz" je izlazna DOBAVNA vrijednost, jedina u istom
  // mjerilu; "Izlaz vr." je prodajna i zato stoji tek iza stanja.
  if (promet) {
    cols.push({key: 'ulazVr', head: n.ulazVr, w: 56, align: 'right', fmt: novac})
    if (poProdajnoj) {
      cols.push({key: 'izlazVr', head: n.izlazVr, w: 66, align: 'right', fmt: novac})
    }
  }
  if (zarada) {
    cols.push({key: 'izlazNabVr', head: 'Izlazna dobavna vrijednost', w: 56, align: 'right', fmt: novac})
  }
  cols.push({key: 'stanjeVr', head: n.stanjeVr, w: 56, align: 'right', fmt: novac})
  if (promet && !poProdajnoj) {
    cols.push({key: 'izlazVr', head: n.izlazVr, w: 66, align: 'right', fmt: novac})
  }
  cols.push({key: 'avgCijena', head: n.prosjCijena, w: 46, align: 'right', fmt: novac})

  if (zarada) {
    cols.push(
      {key: 'zaradaUpisana', head: 'RUC', w: 50, align: 'right', fmt: novac},
      {key: 'rucPosto', head: 'RUC %', w: 38, align: 'right', fmt: posto},
      {key: 'izlazNabVrProsj', head: 'Nab. prosj.', w: 52, align: 'right', fmt: novac},
      {key: 'zaradaProsj', head: 'RUC prosj.', w: 50, align: 'right', fmt: novac},
      {key: 'rucPostoProsj', head: 'RUC % pr.', w: 38, align: 'right', fmt: posto}
    )
  }
  return rasporedi(cols)
}

// Sirine se dijele isto kao prije: Naziv prvi trazi citljivu sirinu, visak ide
// njemu, a kad ne stane - sesnaest stupaca ne stane - sve se skalira istim
// faktorom da tablica padne tocno na stranicu.
const rasporedi = (cols) => {
  // Naziv asks for a readable width first. If everything then fits, the slack goes
  // to Naziv; if it does not - sixteen columns do not - every column is scaled down
  // by the same factor so the table lands exactly on the page instead of running
  // off the right edge.
  const usable = usableWidth(cols.length)
  const flex = cols.find(c => c.flex)
  flex.w = 90
  const total = cols.reduce((s, c) => s + c.w, 0)
  if (total > usable) {
    const scale = usable / total
    cols.forEach(c => { c.w = Math.floor(c.w * scale * 10) / 10 })
  } else {
    flex.w += usable - total
  }
  return cols
}

// Sixteen columns need a smaller type than eight.
const fontFor = (count) => {
  if (count >= 16) { return {body: 6, head: 6} }
  if (count >= 12) { return {body: 7, head: 7} }
  return {body: 8, head: 8}
}

const headerRow = (cols, fs) => cols.map(c => ({
  text: c.head,
  fontSize: fs.head,
  bold: true,
  alignment: c.align,
  fillColor: HEAD_BG,
  margin: [2, 3, 2, 3]
}))

const bodyRow = (cols, item, fs) => cols.map(c => {
  // an article row carries the group in GroupName/itemGroup, a group row in `grupa`
  const raw = c.key === 'grupa'
    ? (item.grupa || item.GroupName || item.itemGroup || '')
    : item[c.key]
  const text = c.fmt ? c.fmt(raw) : String(raw === null || raw === undefined ? '' : raw)
  const cell = {
    text: text,
    fontSize: fs.body,
    alignment: c.align,
    margin: [2, 1.5, 2, 1.5]
  }
  // negative stock and a loss are the two things worth catching on paper
  const gubitak = ['zaradaUpisana', 'zaradaProsj', 'rucPosto', 'rucPostoProsj']
  if ((c.key === 'stanjeKol' && num(raw) < 0) ||
      (gubitak.indexOf(c.key) !== -1 && raw !== null && raw !== undefined && num(raw) < 0)) {
    cell.color = '#c62828'
  }
  return cell
})

const totalRow = (cols, totals, count, fs, grupe) => cols.map((c, index) => {
  let text = ''
  if (index === 0) { text = 'UKUPNO' }
  // in the group view column 1 is the article count, which really does total
  else if (index === 1) { text = grupe ? String(count) : count + ' artikala' }
  else if (c.fmt && totals[c.key] !== undefined) { text = c.fmt(totals[c.key]) }
  return {
    text: text,
    fontSize: fs.body,
    bold: true,
    alignment: c.align,
    fillColor: TOTAL_BG,
    margin: [2, 3, 2, 3]
  }
})

/**
 * @param {Object} ctx
 *   items      the rows as shown on screen (already filtered)
 *   totals     the totals of those rows
 *   company    store.companyActiv
 *   businessYear, fromDate, toDate
 *   promet, zarada    which column groups are on
 *   opsegOpis, wareHouseName, grupa, trazi   printed in the header so the paper
 *                                            says which selection produced it
 */
export const buildStockPdf = (ctx) => {
  const items = ctx.items || []
  const company = ctx.company || {}
  const grupe = ctx.grupe === true
  const cols = buildColumns(ctx.promet !== false, ctx.zarada === true, grupe, {
    ulazVr: ctx.nazivUlazVr,
    stanjeVr: ctx.nazivStanjeVr,
    prosjCijena: ctx.nazivProsjCijena,
    izlazVr: ctx.nazivIzlazVr
  }, ctx.samoVrijednost === true, ctx.poProdajnoj === true)
  const fs = fontFor(cols.length)
  // the group sheet totals articles, the article sheet totals rows
  const brojac = grupe ? (ctx.ukupnoArtikala || 0) : items.length

  const body = [headerRow(cols, fs)]
  items.forEach(i => body.push(bodyRow(cols, i, fs)))
  body.push(totalRow(cols, ctx.totals || {}, brojac, fs, grupe))

  // the selection is spelled out, otherwise a printed page cannot be told apart
  // from one made with different filters
  const uvjeti = []
  if (ctx.opsegOpis) { uvjeti.push(ctx.opsegOpis) }
  // Osnovica mijenja znacenje svih iznosa na papiru, pa mora biti ispisana.
  if (ctx.osnovicaOpis) { uvjeti.push(ctx.osnovicaOpis.toLowerCase()) }
  if (ctx.wareHouseName) { uvjeti.push('skladište: ' + ctx.wareHouseName) }
  if (ctx.grupa) { uvjeti.push('grupa: ' + ctx.grupa) }
  if (ctx.trazi) { uvjeti.push('traži: "' + ctx.trazi + '"') }
  if (ctx.withServices) { uvjeti.push('uključene usluge') }

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [18, 18, 18, 26],
    content: [
      {
        columns: [
          {
            width: '32%',
            text: [
              {text: (company.CompanyName || '') + ' \n', fontSize: 10, bold: true},
              {text: (company.CompanyStreet || '') + ' ' + (company.CompanyBuildingNameNumber || '') + ' \n', fontSize: 7},
              {text: (company.CompanyPostcode || '') + ' ' + (company.CompanyCity || '') + ' \n', fontSize: 7},
              {text: 'OIB: ' + (company.CompanyVATID || ''), fontSize: 7}
            ]
          },
          {
            width: '46%',
            text: [
              {text: (grupe ? 'Stanje skladišta po grupama \n' : 'Stanje skladišta \n'), fontSize: 15, bold: true},
              {text: 'poslovna godina ' + ctx.businessYear + ' \n', fontSize: 10, bold: true},
              {text: 'promet ' + datum(ctx.fromDate) + ' - ' + datum(ctx.toDate) + ' \n', fontSize: 9},
              {text: uvjeti.join(' · '), fontSize: 7, color: '#555555'}
            ]
          },
          {
            width: '22%',
            text: [
              {text: 'Datum ispisa: ' + dayjs().format('DD.MM.YYYY HH:mm') + ' \n', fontSize: 7},
              {text: (grupe ? 'Grupa: ' + items.length + ', artikala: ' + brojac : 'Artikala: ' + items.length), fontSize: 7}
            ]
          }
        ],
        columnGap: 8,
        margin: [0, 0, 0, 8]
      },
      {
        table: {
          headerRows: 1,
          widths: cols.map(c => c.w),
          body: body
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 0.8 : 0.4),
          vLineWidth: () => V_LINE,
          hLineColor: () => LINE,
          vLineColor: () => LINE,
          // the cells pad themselves through the text margins; the default padding
          // would be added to every column width and push the table off the page
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },
      ctx.samoVrijednost === true
      ? {
        text: 'Trenutna vrijednost zalihe: količina stanja puta cijena iz šifrarnika artikala, ' +
          'a ne cijena s dokumenata. Stanje je zaduženje minus razduženje unutar poslovne godine ' +
          ctx.businessYear + '. Iznosi prometa i RUC se u ovom pogledu ne prikazuju jer se čitaju ' +
          's dokumenata, pa bi se dvije različite stvari čitale kao jedna.',
        fontSize: 6,
        color: '#666666',
        margin: [0, 6, 0, 0]
      }
      : {
        text: 'Stanje je zaduženje minus razduženje unutar poslovne godine ' + ctx.businessYear +
          '. Kolone prometa pokrivaju odabrano razdoblje, bez inventure. ' +
          (ctx.nazivProsjCijena || 'Prosj. NC') + ' = (vrijednost početnog stanja + vrijednost ulaza) / njihova količina. ' +
          'Nabavne vrijednosti računaju se iz nabavne cijene puta količina. ' +
          'Izlazna dobavna vrijednost i RUC ostaju na nabavnoj osnovici i kad se zaliha vrednuje prodajnim cijenama. ' +
          'RUC = ostvarena razlika u cijeni = Izlaz vr. (bez PDV-a) − Nab. izlaza; ' +
          'RUC prosj. koristi nabavu po prosječnoj nabavnoj cijeni. ' +
          'RUC % = RUC / nabavna vrijednost izlaza × 100, ista osnovica kao marža na primci; ' +
          'crtica znači da izlaz nema nabavne vrijednosti za dijeliti.',
        fontSize: 6,
        color: '#666666',
        margin: [0, 6, 0, 0]
      }
    ],
    footer: (currentPage, pageCount) => ({
      text: 'Stranica ' + currentPage + ' od ' + pageCount,
      fontSize: 6,
      alignment: 'right',
      margin: [0, 6, 18, 0]
    })
  }

  return docDefinition
}

export const printStockPdf = (ctx) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(buildStockPdf(ctx)).open()
}

export default { buildStockPdf, printStockPdf }
